import "./style.less";
import { Badge, Dropdown, Menu } from "antd";
import {
  AccountIcon,
  AssessmentIcon,
  BagIcon,
  BoldArrowDownIcon,
  CounselingIcon,
  MenuIcon,
  NotificationIcon,
  PGPlainLogo,
} from "../../../../components";
import { useTranslation } from "react-i18next";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import OutsideClickHandler from "react-outside-click-handler";
import clsx from "clsx";
import { VuiNotification } from "../../atoms";
import {
  generateMenu,
  removeAllSymbol,
  usePassportService,
  useWindowSize,
} from "../../../utils";
import { useAppDispatch, useAppSelector } from "../../../../stores/hooks";
import _ from "lodash";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import { update } from "../../../../stores/system";
import { WhatsAppOutlined } from "@ant-design/icons";
import SettingRepository from "../../../../repositories/SettingRepository";

interface IVuiNavbar {
  changeTheme: (value: boolean) => void;
}

const VuiNavbar: FC<IVuiNavbar> = ({ changeTheme }) => {
  const { t } = useTranslation();
  const menus = generateMenu(t);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isLoggedIn, account } = useAppSelector((state) => state.system);
  const { logout } = usePassportService(true);
  const [width, setWidth] = useState<number>(window.innerWidth);
  const [isOpenMobileMenu, setIsOpenMobileMenu] = useState<boolean>(false);
  const [openNotification, setOpenNotification] = useState<boolean>(false);
  const [openDropdownBooking, setOpenDropdownBooking] =
    useState<boolean>(false);
  const isMobile = width < 768;

  const menuItems: ItemType[] = [
    {
      key: "0",
      label: <Link to="/account">{t("common.text.myAccount")}</Link>,
    },
    {
      key: "1",
      label: (
        <Link to="/account?tab=schedule">{t("common.text.schedule")}</Link>
      ),
    },
    { key: "2", label: <div onClick={logout}>{t("common.text.logOut")}</div> },
  ];

  const bookItems: ItemType[] = [
    {
      key: "0",
      label: (
        <Link to="/booking-counseling">
          <div className="vui-navbar-booking-item-wrapper">
            <div className="vui-navbar-booking-item-icon">
              <CounselingIcon />
            </div>
            <div className="vui-navbar-booking-item-text">
              <div className="vui-navbar-booking-item-title">
                {t("common.text.counselingAndPsychotherapy")}
              </div>
              <div className="vui-navbar-booking-item-subtitle">
                {t("common.text.counselingAndPsychotherapySubtitle")}
              </div>
            </div>
          </div>
        </Link>
      ),
    },
    {
      key: "1",
      label: (
        <Link to="/booking-assessment">
          <div className="vui-navbar-booking-item-wrapper">
            <div className="vui-navbar-booking-item-icon">
              <AssessmentIcon />
            </div>
            <div className="vui-navbar-booking-item-text">
              <div className="vui-navbar-booking-item-title">
                {t("common.text.assessmentAndPrograms")}
              </div>
              <div className="vui-navbar-booking-item-subtitle">
                {t("common.text.assessmentSubtitle")}
              </div>
            </div>
          </div>
        </Link>
      ),
    },
  ];

  const handleClickMobileMenu = useCallback(() => {
    if (width <= 768) {
      setIsOpenMobileMenu(!isOpenMobileMenu);
    }
  }, [isOpenMobileMenu, width]);

  const handleCloseMobileMenu = useCallback(() => {
    if (width <= 768) {
      setIsOpenMobileMenu(false);
    }
  }, [isOpenMobileMenu, width]);

  const handleClickNotification = useCallback(() => {
    changeTheme(!openNotification);
    setOpenNotification(!openNotification);
  }, [openNotification, changeTheme]);

  const handleWindowSizeChange = useCallback(() => {
    setWidth(window.innerWidth);
    if (window.innerWidth > 768) {
      setIsOpenMobileMenu(false);
    }
  }, []);

  const handleCloseNotification = useCallback(() => {
    setOpenNotification(false);
    changeTheme(false);
  }, []);

  const [data, setData] = useState<any>(null);
  const loadData = useCallback(async () => {
    await SettingRepository.show()
      .then((response: any) => {
        const { data: responseData } = response.data;
        setData(responseData);
      })
      .catch(() => {});
  }, []);

  const goToWhatsapp = (number: string) => {
    window.open(`https://wa.me/${removeAllSymbol(number)}`, "_blank");
  };

  useMemo(() => {
    (async () => {
      await loadData();
    })();
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  const loadMenus = useMemo(() => {
    return menus.map((menu) => (
      <Link
        className={clsx({
          "vui-navbar-menu-link": true,
          // "has-square": menu.hasSquare,
          active: _.get(location, "pathname", "").includes(menu.link),
        })}
        key={menu.link}
        to={menu.link}
        onClick={() => handleCloseMobileMenu()}
      >
        {menu.name}
      </Link>
    ));
  }, [menus]);

  const handleReadAll = useCallback(() => {
    dispatch(
      update({
        isLoggedIn: isLoggedIn,
        account: {
          ...account,
          total_unread_notification: 0,
        },
      })
    );
  }, [isLoggedIn, account, dispatch]);

  return (
    <div id="vui-navbar">
      <div className="vui-navbar-header-wrapper">
        <div className="container">
          <div className="vui-navbar-header">
            <div className="vui-navbar-header-left">
              <Link to={"/about"}>
                <span className="vui-navbar-header-link">
                  {t("common.text.about")}
                </span>
              </Link>
              <Link to={"/contact"}>
                <span className="vui-navbar-header-link">
                  {t("common.text.contact")}
                </span>
              </Link>
            </div>
            {data && data.whatsapp_number && (
              <div
                style={{ cursor: "pointer" }}
                onClick={() => goToWhatsapp(data.whatsapp_number)}
              >
                <span className="vui-navbar-header-link">
                  <WhatsAppOutlined
                    style={{ color: "#69707A", fontSize: isMobile ? 18 : 16 }}
                  />
                  <span className="flex" style={{ gap: 4 }}>
                    {t("common.text.needAssistance")}?{" "}
                    <strong className="hide-mobile">
                      {data.whatsapp_number}
                    </strong>
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container">
        <div className="vui-navbar-space">
          <PGPlainLogo />

          <div className="vui-navbar-menu-space desktop">
            {loadMenus}
            <Dropdown
              className="vui-navbar-dropdown-booking"
              placement="bottomRight"
              overlay={
                <Menu
                  items={bookItems}
                  className="vui-navbar-dropdown-booking-menu"
                />
              }
              onVisibleChange={(visible) => {
                setOpenDropdownBooking(visible);
              }}
              trigger={["click"]}
            >
              <div
                className="vui-navbar-menu-link has-square"
                onClick={(event) => event.preventDefault()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                }}
              >
                {t("common.text.bookNow")}
                <BoldArrowDownIcon
                  style={{
                    transform: openDropdownBooking
                      ? "rotate(-180deg)"
                      : "rotate(0)",
                    transition: "transform 220ms ease",
                  }}
                />
              </div>
            </Dropdown>
          </div>

          <div className="vui-navbar-menu-space">
            {isLoggedIn ? (
              <>
                <div className="vui-navbar-menu-space-notification">
                  <OutsideClickHandler
                    display="flex"
                    onOutsideClick={() => {
                      handleCloseNotification();
                    }}
                  >
                    <Badge
                      count={account?.total_unread_notification}
                      overflowCount={8}
                      size="small"
                      className="vui-navbar-icon-badge"
                    >
                      <NotificationIcon
                        onClick={() => {
                          handleClickNotification();
                          handleCloseMobileMenu();
                        }}
                      />
                    </Badge>

                    <VuiNotification
                      onReadAll={handleReadAll}
                      show={openNotification}
                    />
                  </OutsideClickHandler>
                </div>

                <Badge
                  count={account?.total_cart}
                  overflowCount={9}
                  size="small"
                  className="vui-navbar-icon-badge"
                >
                  <BagIcon
                    onClick={() => {
                      handleCloseMobileMenu();
                      navigate("/cart?selectAll=true");
                    }}
                  />
                </Badge>
                <Dropdown
                  className="vui-navbar-dropdown-account"
                  placement="bottomRight"
                  overlay={
                    <Menu
                      items={menuItems}
                      className="vui-navbar-dropdown-account-menu"
                    />
                  }
                  trigger={["click"]}
                >
                  <div
                    className="vui-dropdown-account-action"
                    onClick={(event) => event.preventDefault()}
                  >
                    <AccountIcon />
                    <span className="vui-navbar-menu-link hide-mobile">
                      {account?.name}
                    </span>
                  </div>
                </Dropdown>
              </>
            ) : (
              <Link to={"/auth/login"} className="vui-dropdown-account-action">
                <AccountIcon />
                <span className="vui-navbar-menu-link hide-mobile">
                  {t("common.text.login")}
                </span>
              </Link>
            )}

            <MenuIcon
              onClick={handleClickMobileMenu}
              className="vui-navbar-hamburger-icon"
            />
          </div>
        </div>

        <div
          className={clsx({
            "vui-navbar-mobile": true,
            open: isOpenMobileMenu,
          })}
        >
          <div className="vui-navbar-mobile-menu-wrapper">
            {loadMenus}

            <div className="vui-navbar-mobile-menu-booking-divider"></div>

            <div className="vui-navbar-mobile-menu-booking">
              <div className="vui-navbar-mobile-menu-booking-title">
                {t("common.text.bookNow")}
              </div>
              <div className="vui-navbar-mobile-menu-booking-items">
                <Link to="/booking-counseling">
                  <div
                    onClick={(e) => {
                      setIsOpenMobileMenu(false);
                    }}
                    className={clsx({
                      "vui-navbar-booking-item-wrapper": true,
                      active: _.get(location, "pathname", "").includes(
                        "booking-counseling"
                      ),
                    })}
                  >
                    <div className="vui-navbar-booking-item-icon">
                      <CounselingIcon />
                    </div>
                    <div className="vui-navbar-booking-item-text">
                      <div className="vui-navbar-booking-item-title">
                        {t("common.text.counselingAndPsychotherapy")}
                      </div>
                      <div className="vui-navbar-booking-item-subtitle">
                        {t("common.text.counselingAndPsychotherapySubtitle")}
                      </div>
                    </div>
                  </div>
                </Link>
                <Link to="/booking-assessment">
                  <div
                    onClick={(e) => {
                      setIsOpenMobileMenu(false);
                    }}
                    className={clsx({
                      "vui-navbar-booking-item-wrapper": true,
                      active: _.get(location, "pathname", "").includes(
                        "booking-assessment"
                      ),
                    })}
                  >
                    <div className="vui-navbar-booking-item-icon">
                      <AssessmentIcon />
                    </div>
                    <div className="vui-navbar-booking-item-text">
                      <div className="vui-navbar-booking-item-title">
                        {t("common.text.assessmentAndPrograms")}
                      </div>
                      <div className="vui-navbar-booking-item-subtitle">
                        {t("common.text.assessmentSubtitle")}
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VuiNavbar;
